import * as React from 'react';
import { SEARCH_PARAMS_PAGE } from '@avensia/excite-esales';
import { isPage, type PageOrError, currentUrl, historyEvent, replaceState, currentPage } from '@avensia/excite';
import { throttle } from './throttle';

export const SEARCH_PARAMS_INFINITE = 'infinite';

let isLoadingMoreProducts = false;
const loadingMarginTop = 800;
let unbindInfiniteScroll: undefined | (() => void);

function onScroll(element: HTMLElement) {
  const clientHeight = document.documentElement.clientHeight;
  const elementPositionY = element.offsetTop + element.clientTop + element.clientHeight;
  let scrollPos: number;
  if (document.documentElement.scrollTop) {
    scrollPos = document.documentElement.scrollTop;
  } else {
    scrollPos = window.pageYOffset;
  }
  if (elementPositionY - loadingMarginTop <= scrollPos + clientHeight && elementPositionY >= scrollPos) {
    isLoadingMoreProducts = true;
    loadMoreProducts().finally(() => {
      isLoadingMoreProducts = false;
    });
  }
}

function bind(element: HTMLElement) {
  const listener = throttle(() => {
    if (!isLoadingMoreProducts) {
      onScroll(element);
    }
  }, 200);
  window.addEventListener('scroll', listener);

  unbindInfiniteScroll = () => {
    window.removeEventListener('scroll', listener);
    unbindInfiniteScroll = undefined;
  };
}

export function useInfiniteScroll<T extends HTMLElement>() {
  const ref = React.useRef<T>(null);
  React.useEffect(() => {
    return historyEvent.on(e => {
      if (!e.url.hiddenParams.has(SEARCH_PARAMS_INFINITE)) {
        unbindInfiniteScroll?.();
      }

      if (currentUrl().searchParams.has(SEARCH_PARAMS_PAGE)) {
        const page = Number(currentUrl().searchParams.get(SEARCH_PARAMS_PAGE));
        const pageCount = (currentPage.currentSync() as any)?.pagination?.pageCount ?? 0;
        if (page >= pageCount) {
          unbindInfiniteScroll?.();
        }
      }
    });
  }, []);

  const bindInfiniteScroll = React.useCallback(() => {
    if (ref.current && !unbindInfiniteScroll) {
      loadMoreProducts().finally(() => {
        isLoadingMoreProducts = false;
      });

      bind(ref.current);
    }
  }, []);

  return { ref, bindInfiniteScroll, unbindInfiniteScroll };
}

function merge(page: PageOrError, response: Excite.ContentApi.InfiniteScrollApiModel<any>) {
  return isPage(page)
    ? {
        ...page,
        products: [...(page as any).products, ...response.products],
        url: currentUrl(),
        pagination: { ...response.pagination },
      }
    : page;
}
export function loadMoreProducts() {
  const newUrl = currentUrl();
  const page = newUrl.searchParams.has(SEARCH_PARAMS_PAGE)
    ? Number(newUrl.searchParams.get(SEARCH_PARAMS_PAGE)) + 1
    : 2;
  newUrl.searchParams.set(SEARCH_PARAMS_PAGE, String(page));
  if (!newUrl.hiddenParams.has(SEARCH_PARAMS_INFINITE)) {
    newUrl.hiddenParams.append(SEARCH_PARAMS_INFINITE, '1');
  }

  return replaceState(newUrl, { skipUpdateScrollPosition: true, merge, shouldUseCache: () => false });
}
